/**
 * `useWriteStatus` — write-gate stav aktuálneho podpisovateľa (dizajn B10).
 *
 * Goľovina bráni zápis na `POST /events` len registrovaným operátorom /
 * agentom; UI sa pýta predom, aby zbytočne neposielal eventy, ktoré dostanú
 * 403. `GET {gateway}/write-status?pubkey=<hex>` vracia
 * `{ok, pubkey, allowed, reason, via, operator}` (route je v
 * `gateway/src/register-routes.ts`, rozhoduje `write-gate.ts`).
 *
 * Pubkey berieme od SKUTOČNÉHO signera (`useSigner`, NIP-07 má prednosť pred
 * `nsec` identitou) — gateway hodnotí gate podľa `pubkey` podpisu, nie podľa
 * `identity.pubkey`. Fallback na identitu drží UI funkčný, kým NIP-07
 * resolve prebieha; po jej dobehnutí sa stav automaticky premeria znova
 * (`pubkey` je v deps).
 *
 * Fail-closed:
 *  - fetch zlyhá (offline / 4xx / 5xx) → `allowed=false`, `kind='unavailable'`
 *    a NEUTRÁLNY reason (`WRITE_STATUS_UNAVAILABLE`) — banner nesmie tvrdiť,
 *    že používáč nie je operátor, keď to len nevieme;
 *  - `kind='checking'` composer nezmáčkne (inak by blikal pri každom
 *    refetci a narúšal psanie); server aj tak rozhoduje, 403 dôvod sa
 *    ukáže z response body (`ApiError.reasons`).
 */
import { useCallback, useEffect, useState } from 'react';

import { useApi } from '../api/ApiContext.js';
import { useIdentity } from '../nostr/IdentityContext.js';
import { useSigner } from '../nostr/useSigner.js';
import type { WriteStatusResponse } from '../api/types.js';

/** úvodný topic inštancie — banner naň ukazuje ako „ako sa pripojiť“ */
export const HOW_IT_WORKS_SLUG = 'o-com-funguje-forum';

/** neutrálny reason pri zlyhaní — UI ho ukazuje, lebo nič iné nevieme */
export const WRITE_STATUS_UNAVAILABLE = 'write status unavailable — gateway neodpovedal';

export type WriteStatusKind = 'anonymous' | 'checking' | 'allowed' | 'denied' | 'unavailable';

export interface WriteStatusState {
  /** pubkey podpisovateľa, alebo null kým nejaky nie je */
  pubkey: string | null;
  kind: WriteStatusKind;
  /** fail-closed: `true` len keď gateway povedal `allowed: true` */
  allowed: boolean;
  /** composer je zamknutý a banner sa ukáže: `denied` alebo `unavailable` */
  locked: boolean;
  /** reason z gatewayu, `WRITE_STATUS_UNAVAILABLE`, alebo null */
  reason: string | null;
  /** `via` z gatewayu (bootstrap/operator/agent/none) — pre kontext UI */
  via: WriteStatusResponse['via'] | null;
  /** vynúti nové meranie (napr. po publishi, ktorý dostal 403) */
  refresh: () => void;
}

export function useWriteStatus(): WriteStatusState {
  const api = useApi();
  const { identity } = useIdentity();
  const { signer } = useSigner();
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<Omit<WriteStatusState, 'refresh'>>({
    pubkey: null,
    kind: 'anonymous',
    allowed: false,
    locked: false,
    reason: null,
    via: null,
  });

  // podpisovateľ (NIP-07) má prednosť pred nsec identitou — presne tento
  // pubkey uvidí gateway na POST /events
  const pubkey = signer?.pubkey ?? identity?.pubkey ?? null;

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (pubkey === null) {
      setState({ pubkey: null, kind: 'anonymous', allowed: false, locked: false, reason: null, via: null });
      return;
    }
    let cancelled = false;
    // `checking` NEMENÍ `locked` — composer zostáva v stave, v akom bol,
    // kým nedorazí odpoveď (žiadne blikanie tlačidiel pri refetci)
    setState((prev) => ({ ...prev, pubkey, kind: 'checking' }));
    api
      .writeStatus(pubkey)
      .then((status) => {
        if (cancelled) return;
        setState({
          pubkey,
          kind: status.allowed ? 'allowed' : 'denied',
          allowed: status.allowed === true,
          locked: status.allowed !== true,
          reason: status.reason,
          via: status.via ?? null,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({
          pubkey,
          kind: 'unavailable',
          allowed: false,
          locked: true,
          reason: WRITE_STATUS_UNAVAILABLE,
          via: null,
        });
      });
    return () => {
      cancelled = true;
    };
    // `pubkey` v deps = refresh po zmene identity / signera
  }, [api, pubkey, nonce]);

  return { ...state, refresh };
}
